import React, { useMemo } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { getRouteApi } from "@tanstack/react-router";
// ui
import { DatePickerInput } from "@mantine/dates";
import {
  Box,
  Group,
  SimpleGrid,
  Text,
  SegmentedControl,
  Button,
} from "@mantine/core";
import { IconPhoto } from "@tabler/icons-react";
// types
import type {
  TimeGranularity,
  TimePeriod,
} from "@utils/chart-data-conversions/timelines";

const route = getRouteApi("/_authenticated/app/players/$playerId/insights");

const MAX_NUM_COLUMNS = 180;

const willGranularityResultInFewEnoughColumns = (
  start: DateTime,
  end: DateTime,
  granularity: TimeGranularity,
): boolean => {
  return end.diff(start, granularity)[`${granularity}s`] <= MAX_NUM_COLUMNS;
};

const transformDate = (val: Date | null): string =>
  val ? DateTime.fromJSDate(val).toFormat("yyyy-MM-dd") : "";

interface QuickPickButtonProps {
  period: TimePeriod;
  label: string;
}

const QuickPickButton: React.FC<QuickPickButtonProps> = (props) => {
  const navigate = route.useNavigate();

  return (
    <Button
      leftSection={<IconPhoto size={14} />}
      variant="light"
      size="xs"
      onClick={async (event) => {
        event.preventDefault();
        await navigate({
          search: (current) => ({
            ...current,
            startDate: props.period.start.toFormat("yyyy-MM-dd"),
            endDate: props.period.end.toFormat("yyyy-MM-dd"),
            granularity: props.period.granularity,
          }),
        });
      }}
    >
      {props.label}
    </Button>
  );
};

export const DateRangeSelect: React.FC = () => {
  const { t } = useTranslation();
  const navigate = route.useNavigate();

  const {
    granularity,
    startDate: startDateStr,
    endDate: endDateStr,
  } = route.useSearch();

  const startDate = DateTime.fromISO(startDateStr);
  const endDate = DateTime.fromISO(endDateStr);

  const granularityOpts: TimeGranularity[] = useMemo(() => {
    const ret: TimeGranularity[] = [];

    const allOpts: TimeGranularity[] = ["day", "week", "month"];
    allOpts.forEach((opt) => {
      if (willGranularityResultInFewEnoughColumns(startDate, endDate, opt)) {
        ret.push(opt);
      }
    });

    return ret;
  }, [endDate, startDate]);

  const quickPicks = useMemo<QuickPickButtonProps[]>(() => {
    const now = DateTime.local();

    const opts: QuickPickButtonProps[] = [
      {
        label: t("playerInsights.configureSection.thisWeek"),
        period: {
          start: now.startOf("week"),
          end: now.endOf("week"),
          granularity: "day",
        },
      },
      {
        label: t("playerInsights.configureSection.lastWeek"),
        period: {
          start: now.minus({ weeks: 1 }).startOf("week"),
          end: now.minus({ weeks: 1 }).endOf("week"),
          granularity: "day",
        },
      },
    ];

    const months = [1, 3, 6];

    months.forEach((m) => {
      opts.push({
        label:
          m === 1
            ? t("playerInsights.configureSection.oneMonth")
            : t("playerInsights.configureSection.nMonths", { count: m }),
        period: {
          start: now.minus({ month: m }).startOf("day"),
          end: now.endOf("day"),
          granularity: "week",
        },
      });
    });

    return opts;
  }, [t]);

  return (
    <SimpleGrid cols={{ xs: 1, md: 3 }}>
      <Box>
        <Text>{t("playerInsights.configureSection.quickSelect")}</Text>
        <Group>
          {quickPicks.map((qp, idx) => (
            <QuickPickButton {...qp} key={idx} />
          ))}
        </Group>
      </Box>

      <Box>
        <Text fw="bold">
          {t("playerInsights.configureSection.datePickerTitle")}
        </Text>
        <DatePickerInput
          label={t("playerInsights.configureSection.startDate")}
          value={startDate?.toJSDate() ?? null}
          onChange={async (newVal) => {
            await navigate({
              search: (current) => ({
                ...current,
                startDate: transformDate(newVal),
              }),
            });
          }}
        />
        <DatePickerInput
          label={t("playerInsights.configureSection.endDate")}
          value={endDate?.toJSDate() ?? null}
          onChange={async (newVal) => {
            await navigate({
              search: (current) => ({
                ...current,
                endDate: transformDate(newVal),
              }),
            });
          }}
        />
      </Box>

      <Box>
        <Text>{t("playerInsights.configureSection.granularity")}</Text>
        <SegmentedControl
          data={granularityOpts}
          value={granularity}
          onChange={async (val) => {
            await navigate({
              search: (current) => ({
                ...current,
                granularity: val,
              }),
            });
          }}
        />
      </Box>
    </SimpleGrid>
  );
};
